import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { HotKeys } from 'react-hotkeys';
import classNames from 'classnames';
import VimeoPlayer from '@vimeo/player';

import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import Spinner from '../../../global/Spinner';
import ArrowLeft from '../../../icons/ArrowLeft';
import DismissButton from '../../../global/DismissButton';
import ImagePreload from '../../../global/ImagePreload';
import { getThumbBySize } from '../../../../util/photo';
import Button from '../../../global/Button';
import { getAttributionName } from '../../../../util/account';
import PauseButtonIcon from '../../../icons/PauseButtonIcon';
import PlayButtonIcon from '../../../icons/PlayButtonIcon';
import ContributorProfileBadge from '../../../global/ContributorProfileBadge';

export const LOAD_MORE_THRESHOLD = 5;

const propTypes = {
  recordId: PropTypes.string.isRequired,
  records: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ),
  loadMore: PropTypes.func,
  shouldLoadMore: PropTypes.bool,
  recordVideoAccessor: PropTypes.string,
  renderVideo: PropTypes.func,
  actions: PropTypes.func,
  footer: PropTypes.func,
  imageSizeCode: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
  gridPath: PropTypes.string,
};

const VideoGridViewer = ({
  records,
  recordId,
  shouldLoadMore,
  loadMore,
  recordVideoAccessor,
  renderVideo,
  imageSizeCode = IMAGE_SIZE_CODE.LARGE,
  actions,
  footer,
  gridPath,
}) => {
  const [loading, setLoading] = useState(false);
  const [detailsHidden, setDetailsHidden] = useState(true);
  const match = useRouteMatch();
  const history = useHistory();
  const { url } = match;
  const basePath = url.split('/').slice(0, -1).join('/');
  const videoElementRef = useRef(null);
  const vimeoPlayerRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const setupVimeoPlayer = () => {
    vimeoPlayerRef.current = new VimeoPlayer(videoElementRef.current);
    vimeoPlayerRef.current.on('play', () => {
      setIsVideoPlaying(true);
    });
    vimeoPlayerRef.current.on('pause', () => {
      setIsVideoPlaying(false);
    });
  };

  /**
   * Checks if more records are necessary to load and loads them
   * if we're approaching the end of the navigation (right-hand-wise)
   * so we would need to load more records to continue to the next record.
   *
   * @returns {Promise<void>}
   */
  const maybeLoadMoreRecords = async () => {
    if (loading || !loadMore || !shouldLoadMore) {
      return;
    }

    const currentRecordIndex = records.findIndex(({ id }) => recordId === id);

    // If we're approaching the end and there are more records, load them
    // to keep a steady progression to the next record
    if (
      records.length - currentRecordIndex <= LOAD_MORE_THRESHOLD ||
      // If we land on a photo not loaded yet, we need to query stuff until we get it
      currentRecordIndex === -1
    ) {
      setLoading(true);
      await loadMore();
      setLoading(false);
    }
  };

  useEffect(() => {
    maybeLoadMoreRecords();
  }, [recordId, records.length, loading]);

  /**
   * Gets the adjacent record ID (ie previous or next one).
   *
   * @param {number} adjacency - 1 for "next" or -1 for "previous".
   * @returns {string|undefined}
   */
  const getAdjacentRecordId = adjacency => {
    const currentRecordIndex = records.findIndex(({ id }) => id === recordId);
    const nextRecord = records[currentRecordIndex + 1];
    const prevRecord = records[currentRecordIndex - 1];

    if (currentRecordIndex === -1) {
      return undefined;
    }

    if (adjacency === 1 && nextRecord) {
      return nextRecord.id;
    }

    if (adjacency === -1 && prevRecord) {
      return prevRecord.id;
    }

    return undefined;
  };

  /**
   * Navigates through records (ie previous or next).
   *
   * @param {number} adjacency - 1 for "next" or -1 for "previous".
   */
  const onNavigateRecords = adjacency => {
    const nextRecordId = getAdjacentRecordId(adjacency);

    if (!nextRecordId) {
      return;
    }

    history.replace(`${basePath}/${nextRecordId}`);
  };

  /**
   * Dismisses the Photo Viewer
   */
  const onDismiss = () => {
    history.replace(gridPath);
  };

  /**
   * Helper method that navigates to either the next or prev record
   * depending on whether they exist (with that priority) or dismisses
   * the video viewer in case neither exist.
   */
  const onNavigateNextAfterRemoval = () => {
    const nextRecordId = getAdjacentRecordId(1);

    if (!nextRecordId) {
      const prevRecordId = getAdjacentRecordId(-1);

      if (!prevRecordId) {
        onDismiss();
      } else {
        history.replace(`${basePath}/${prevRecordId}`);
      }

      return;
    }

    history.replace(`${basePath}/${nextRecordId}`);
  };

  const getVideoFromRecord = record => {
    if (!record) {
      return undefined;
    }

    return recordVideoAccessor ? record?.[recordVideoAccessor] : record;
  };

  const record = records.find(({ id }) => recordId === id);
  const nextRecordId = getAdjacentRecordId(1);
  const prevRecordId = getAdjacentRecordId(-1);

  const video = getVideoFromRecord(record);
  const nextVideo = getVideoFromRecord(
    records.find(({ id }) => nextRecordId === id),
  );

  const detailsClasses = classNames({
    'video-details': true,
    hidden: detailsHidden,
  });

  const uploaderAccount = video?.contributor_account ?? video?.account;

  useEffect(() => {
    if (videoElementRef?.current) {
      // Create a vimeoPlayerRef:
      setupVimeoPlayer();
    }
  }, [video]);

  return (
    <Modal
      ariaHideApp={false}
      isOpen
      bodyOpenClassName="body-modal-open"
      overlayClassName="video-grid-viewer-overlay"
      className="video-grid-viewer"
    >
      <div className="video-grid-viewer-header">
        <div className="video-grid-viewer-header-left">
          <DismissButton
            className="video-grid-viewer-btn-dismiss"
            onClick={onDismiss}
          />
        </div>

        {!!record && actions && (
          <div className="video-grid-viewer-header-right">
            {actions({
              record,
              onPrevRecord: () => onNavigateRecords(-1),
              onNextRecord: () => onNavigateRecords(1),
              onNavigateNextAfterRemoval,
              nextRecordId,
              prevRecordId,
            })}
          </div>
        )}
      </div>

      <button
        className="video-grid-viewer-nav video-grid-viewer-nav-prev"
        onClick={() => onNavigateRecords(-1)}
        disabled={!prevRecordId}
      >
        <span className="video-grid-viewer-nav-outline">
          <ArrowLeft color="#fff" />
        </span>
      </button>
      <div className="video-grid-viewer-body">
        <div className="video-grid-viewer-content">
          {video ? (
            (() => {
              if (renderVideo) {
                return renderVideo({ video, record });
              }

              return (
                <div className="video-viewer-main">
                  <div className="video-viewer-iframe-wrapper">
                    <iframe
                      className="video-viewer-iframe"
                      title={video.name}
                      src={`${video.embed_url}&controls=false`}
                      allowFullScreen
                      webkitallowfullscreen="true"
                      mozallowfullscreen="true"
                      allow="autoplay; encrypted-media"
                      ref={videoElementRef}
                    />

                    <Button
                      btnStyle="reset"
                      btnSize="sm"
                      className="video-viewer-play-button"
                      onClick={() => {
                        if (!vimeoPlayerRef.current) {
                          setupVimeoPlayer();
                        }

                        if (isVideoPlaying) {
                          vimeoPlayerRef?.current?.pause();
                        } else {
                          vimeoPlayerRef?.current?.play();
                        }
                      }}
                    >
                      {isVideoPlaying ? (
                        <div className="video-viewer-play-button-icon-wrapper video-viewer-play-button-playing">
                          <PauseButtonIcon size={40} color="#fff" />
                        </div>
                      ) : (
                        <div className="video-viewer-play-button-icon-wrapper">
                          <PlayButtonIcon size={40} color="#fff" />
                        </div>
                      )}
                    </Button>
                  </div>
                  <div className={detailsClasses}>
                    {uploaderAccount && (
                      <ContributorProfileBadge
                        displayName={getAttributionName(uploaderAccount)}
                        displayPhotoSrc={
                          getThumbBySize(
                            uploaderAccount?.avatar_photo?.thumbnail_urls,
                            IMAGE_SIZE_CODE.SMALL,
                          )?.url ?? undefined
                        }
                        prependText="By"
                        className="light mb-1"
                        accountId={video?.attribution?.account_id}
                        photoAttributionPageEnabled={
                          video?.attribution?.photo_attribution_page?.is_enabled
                        }
                      />
                    )}
                    <div className="d-flex mb-1">
                      <h3 className="mb-0 mr-1">{video.name}</h3>
                      <Button
                        btnStyle="reset"
                        onClick={() => setDetailsHidden(!detailsHidden)}
                        className="text-bold text-white ml-auto align-items-center"
                      >
                        {detailsHidden ? 'Show Details' : 'Hide'}
                      </Button>
                    </div>
                    <p>{video.description}</p>
                  </div>

                  <div className="photo-grid-viewer-footer">
                    {!!records && footer && (
                      <>
                        {footer({
                          record,
                          onPrevRecord: () => onNavigateRecords(-1),
                          onNextRecord: () => onNavigateRecords(1),
                          onNavigateNextAfterRemoval,
                          nextRecordId,
                          prevRecordId,
                        })}
                      </>
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            <Spinner />
          )}
        </div>
      </div>
      <button
        className="video-grid-viewer-nav video-grid-viewer-nav-next"
        onClick={() => onNavigateRecords(1)}
        disabled={!nextRecordId}
      >
        <span className="video-grid-viewer-nav-outline">
          <ArrowLeft color="#fff" />
        </span>
      </button>

      {nextVideo && (
        <ImagePreload
          urls={[getThumbBySize(nextVideo.thumbnail_urls, imageSizeCode)?.url]}
        />
      )}

      <HotKeys
        handlers={{
          esc: onDismiss,
          escape: onDismiss,
          right: nextRecordId ? () => onNavigateRecords(1) : undefined,
          left: prevRecordId ? () => onNavigateRecords(-1) : undefined,
        }}
        attach={window}
        focused
      />
    </Modal>
  );
};

VideoGridViewer.propTypes = propTypes;

export default VideoGridViewer;
